// r24 Product Catalog — Sworn Translator Contact-only CTA (phone / LINE / email).
import { Phone, MessageCircle, Mail } from "lucide-react";
import { BRAND_PHONE, BRAND_EMAIL } from "@/lib/brand";
import type { SwornTranslatorProduct } from "@/data/sworn-translator-products";

const LINE_URL = "https://line.me/ti/p/@nycli";

interface Props {
  product: SwornTranslatorProduct;
  locale: "th" | "en";
  variant?: "inline" | "sticky-mobile";
}

export default function SwornTranslatorProductCTA({ product, locale, variant = "inline" }: Props) {
  const isTh = locale === "th";
  const name = isTh ? product.nameTh : product.nameEn;
  const subject = encodeURIComponent(isTh ? `สอบถามแปลสาบาน: ${name} (${product.sku})` : `Sworn translation enquiry: ${name} (${product.sku})`);
  const body = encodeURIComponent(isTh
    ? `สวัสดีค่ะ/ครับ\nต้องการแปลสาบาน: ${name}\nSKU: ${product.sku}\nภาษาปลายทาง: \nประเทศปลายทาง: \nจำนวนหน้า: \nวันที่ต้องการรับ: \n(แนบไฟล์สแกนต้นฉบับ)`
    : `Hello,\nI would like a sworn translation of: ${name}\nSKU: ${product.sku}\nTarget language: \nDestination country: \nPage count: \nRequired by: \n(Please attach scans)`);
  const lineText = encodeURIComponent(isTh ? `สอบถามแปลสาบาน: ${name} (${product.sku})` : `Sworn translation enquiry: ${name} (${product.sku})`);

  const btnBase = "inline-flex items-center justify-center gap-2 rounded-xl font-semibold shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md min-h-[44px] px-4 py-3 text-sm";
  const labels = isTh ? { call: "โทรสอบถาม", line: "แชท LINE", email: "ส่งอีเมล" } : { call: "Call to enquire", line: "Chat on LINE", email: "Email us" };

  if (variant === "sticky-mobile") {
    return (
      <div className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-3 gap-1.5 border-t border-slate-200 bg-white/95 p-2 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] backdrop-blur md:hidden" role="group" aria-label={isTh ? "ช่องทางติดต่อ" : "Contact channels"}>
        <a href={`tel:${BRAND_PHONE.replace(/[^+\d]/g, "")}`} className={`${btnBase} bg-blue-700 text-white`} data-cta="phone" aria-label={labels.call}>
          <Phone className="h-4 w-4" /> <span className="text-[13px]">{labels.call}</span>
        </a>
        <a href={`${LINE_URL}?text=${lineText}`} target="_blank" rel="noopener" className={`${btnBase} bg-[#06C755] text-white`} data-cta="line" aria-label={labels.line}>
          <MessageCircle className="h-4 w-4" /> <span className="text-[13px]">LINE</span>
        </a>
        <a href={`mailto:${BRAND_EMAIL}?subject=${subject}&body=${body}`} className={`${btnBase} bg-slate-800 text-white`} data-cta="email" aria-label={labels.email}>
          <Mail className="h-4 w-4" /> <span className="text-[13px]">Email</span>
        </a>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label={isTh ? "ช่องทางติดต่อ" : "Contact channels"}>
      <a href={`tel:${BRAND_PHONE.replace(/[^+\d]/g, "")}`} className={`${btnBase} bg-blue-700 text-white hover:bg-blue-800`} data-cta="phone">
        <Phone className="h-4 w-4" /> {labels.call} <span className="opacity-80">{BRAND_PHONE}</span>
      </a>
      <a href={`${LINE_URL}?text=${lineText}`} target="_blank" rel="noopener" className={`${btnBase} bg-[#06C755] text-white hover:brightness-95`} data-cta="line">
        <MessageCircle className="h-4 w-4" /> {labels.line}
      </a>
      <a href={`mailto:${BRAND_EMAIL}?subject=${subject}&body=${body}`} className={`${btnBase} bg-slate-800 text-white hover:bg-slate-900`} data-cta="email">
        <Mail className="h-4 w-4" /> {labels.email}
      </a>
    </div>
  );
}
